/**
 * Zentrale Anzeige für Immobilien in Auswahl-Dropdowns (NT-POLISH-01, P1).
 *
 * Einzige Stelle, die den Options-Text und die LAGER/VERSTECKE-Gruppierung erzeugt —
 * Markt-Kauf, Sendungen, Transporte, Dealer, Auftragsbrett, Kontakte nutzen alle dieses Modul.
 *
 * Format: "Eigenname — Typ (X frei)", ohne Eigenname "Typ (X frei)". Kein "VERSTECK –"-Präfix
 * mehr — die Kategorie steckt in der <optgroup>.
 */

import { formatWarehouseName } from './formatters.js';

export interface PropertySelectItem {
  id:          string;
  /** Typ-Label vom Backend (z. B. "Kellerstudio") oder roher subType — beides wird lesbar gemacht. */
  label?:      string | null;
  subType?:    string | null;
  customName?: string | null;
  nickname?:   string | null;
  isHideout:   boolean;
  /** null/undefined = keine Frei-Anzeige (z. B. Ziel ohne bekannte Kapazität). */
  freeSlots?:  number | null;
}

/** Lesbares Typ-Label (Garagenbox, Kellerstudio …). */
export function formatPropertyTypeLabel(p: Pick<PropertySelectItem, 'label' | 'subType' | 'isHideout'>): string {
  const raw = p.label ?? p.subType ?? '';
  if (raw) return formatWarehouseName(raw);
  return p.isHideout ? 'Versteck' : 'Lager';
}

/** Eigenname — Typ (ohne Frei-Anzeige). Eigenname = customName, sonst Spitzname. */
export function formatPropertyDisplayName(p: PropertySelectItem): string {
  const type = formatPropertyTypeLabel(p);
  const own  = (p.customName ?? p.nickname ?? '').trim();
  return own ? `${own} — ${type}` : type;
}

/** Vollständiger Options-Text inkl. "(X frei)". */
export function formatPropertyOptionLabel(p: PropertySelectItem): string {
  const name = formatPropertyDisplayName(p);
  if (p.freeSlots === null || p.freeSlots === undefined) return name;
  return `${name} (${String(Math.max(0, p.freeSlots))} frei)`;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export interface PropertyOptionsOptions {
  selectedId?:  string | null;
  /** Optionaler Platzhalter (value=""), wird vor den Gruppen ausgegeben. */
  placeholder?: string;
  /** Optionaler Text-Transform (z. B. für Stellen, die alles in Versalien zeigen). */
  upperCase?:   boolean;
  /** Pro Eintrag deaktivieren (z. B. voll). */
  isDisabled?:  (p: PropertySelectItem) => boolean;
  /** Ersetzt die "(X frei)"-Anzeige durch einen eigenen Zusatz (z. B. "(12 ITEMS)"). */
  suffix?:      (p: PropertySelectItem) => string;
}

/**
 * Rendert <option>s gruppiert in <optgroup label="LAGER"> / <optgroup label="VERSTECKE">.
 * Leere Gruppen werden weggelassen.
 */
export function renderPropertyOptions(items: readonly PropertySelectItem[], opts: PropertyOptionsOptions = {}): string {
  const renderOne = (p: PropertySelectItem): string => {
    const text = opts.suffix ? `${formatPropertyDisplayName(p)} ${opts.suffix(p)}` : formatPropertyOptionLabel(p);
    const label = opts.upperCase ? text.toUpperCase() : text;
    const selected = opts.selectedId !== undefined && opts.selectedId !== null && p.id === opts.selectedId ? ' selected' : '';
    const disabled = opts.isDisabled?.(p) ? ' disabled' : '';
    return `<option value="${esc(p.id)}"${selected}${disabled}>${esc(label)}</option>`;
  };
  const lager     = items.filter((p) => !p.isHideout);
  const verstecke = items.filter((p) => p.isHideout);
  const placeholder = opts.placeholder !== undefined
    ? `<option value="" disabled${!opts.selectedId ? ' selected' : ''}>${esc(opts.placeholder)}</option>`
    : '';
  const groups = [
    lager.length > 0 ? `<optgroup label="LAGER">${lager.map(renderOne).join('')}</optgroup>` : '',
    verstecke.length > 0 ? `<optgroup label="VERSTECKE">${verstecke.map(renderOne).join('')}</optgroup>` : '',
  ];
  return placeholder + groups.join('');
}
